import cx from 'classnames';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Resizable } from 're-resizable';
import React, { ReactNode } from 'react';
import styles from '../layout.module.scss';

const SIDE_PANEL_EXPANDED_MIN_WIDTH_PX = 180;
const SIDE_PANEL_COLLAPSED_WIDTH_PX = 0;
const SIDE_PANEL_EXPANDED_MAX_WIDTH_PX = 500;

export interface LeftSideBarProps {
  /**
   * Custom content to render inside Left Panel.
   */
  children: ReactNode;

  /**
   * Minimum width (in pixels) upto which Left Panel can be Resized.
   * Only applicable when isLeftPanelResizable is set to true.
   * @default '180'
   */
  leftPanelMinWidth?: number;

  /**
   * Maximum width (in pixels) upto which Left Panel can be Resized.
   * Only applicable when isLeftPanelResizable is set to true.
   * @default '500'
   */
  leftPanelMaxWidth?: number;

  /**
   * For a Floating Left Panel. Only applicable when the panel is expanded by setting isLeftPanelExpanded to true.
   */
  isLeftPanelFloating: boolean;

  /**
   * To show/hide Left Panel.
   */
  isLeftPanelExpanded: boolean;

  /**
   * To make the Left Panel Resizable.
   * @default false
   */
  isLeftPanelResizable?: boolean;

  /**
   * Override class
   */
  className?: string;
}

export const LeftSideBar: React.FC<LeftSideBarProps> = ({
  children,
  isLeftPanelExpanded,
  isLeftPanelFloating,
  leftPanelMinWidth = SIDE_PANEL_EXPANDED_MIN_WIDTH_PX,
  leftPanelMaxWidth = SIDE_PANEL_EXPANDED_MAX_WIDTH_PX,
  isLeftPanelResizable = false,
  className,
}: LeftSideBarProps) => {
  const leftPanelFloatingMinWidth = SIDE_PANEL_COLLAPSED_WIDTH_PX;
  const leftPanelFloatingMaxWidth = SIDE_PANEL_COLLAPSED_WIDTH_PX;

  return (
    <Resizable
      as="section"
      className={cx(
        styles.sidePanel,
        styles.leftPanel,
        {
          [styles.floating]: isLeftPanelFloating,
          [styles.expanded]: isLeftPanelExpanded,
        },
        className
      )}
      minWidth={isLeftPanelExpanded ? leftPanelMinWidth : leftPanelFloatingMinWidth}
      maxWidth={isLeftPanelExpanded ? leftPanelMaxWidth : leftPanelFloatingMaxWidth}
      defaultSize={{ width: '260px', height: 'auto' }}
      enable={{
        top: false,
        right: isLeftPanelExpanded && isLeftPanelResizable,
        bottom: false,
        left: false,
        topRight: false,
        bottomRight: false,
        bottomLeft: false,
        topLeft: false,
      }}
      handleComponent={{
        right: <div />,
      }}
      handleClasses={{
        right: styles.resizeHandle,
      }}
    >
      <OverlayScrollbarsComponent
        options={{
          scrollbars: { autoHide: 'move' },
        }}
        className={styles.sidebarOverlayScroll}
      >
        {/* innerContainer should be it's own reusable container component */}
        <div className={styles.innerContainer}>
          {/* {left panel children} */}
          {/* remove the div below this comment */}
          {children}
        </div>
      </OverlayScrollbarsComponent>
    </Resizable>
  );
};
